import { FC } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { useSessionStorage } from 'usehooks-ts';

import {
  RecordsWithTimestamp,
  SAVED_INSTANCES_KEY,
  SaveToolButton,
  SaveToolButtonCallbacks,
  SearchInstanceDto,
} from '@app/components';
import { useAppContext } from '@app/contexts';
import {
  useSavedInstancesCompact,
  useSavedInstancesFetch,
  useSaveInstance,
  useUnSaveInstance,
} from '@app/hooks';
import { addEntryToDashboardList } from '@app/utils';

interface SavedInstanceToolButtonProps {
  entry: SearchInstanceDto;
  idKey?: 'id' | 'instanceId';
  isItemRemovable?: boolean;
}

export const SavedInstanceToolButton: FC<SavedInstanceToolButtonProps> = ({
  entry,
  idKey = 'id',
  isItemRemovable = false,
}) => {
  const queryClient = useQueryClient();
  const { isLoggedIn } = useAppContext();
  const {
    data: savedInstancesCompactList = {},
    isInitialLoading: isSavedInstancesCompactInitialLoading,
    refetch: refetchSavedInstancesCompactList,
  } = useSavedInstancesCompact();
  const [savedInstances, setSavedInstances] = useSessionStorage<
    RecordsWithTimestamp[]
  >(SAVED_INSTANCES_KEY, []);
  const savedRecordsQuery = useSavedInstancesFetch();
  const saveInstanceMutation = useSaveInstance();
  const unsaveInstanceMutation = useUnSaveInstance();

  const isSelected = isLoggedIn
    ? Boolean(savedInstancesCompactList[entry[idKey]])
    : savedInstances.some(instance => instance.instanceId === entry[idKey]);

  const removeSavedInstanceFromCache = (list: RecordsWithTimestamp[] = []) => {
    if (isItemRemovable) {
      const key = list.filter(item => item.instanceId !== entry.id);
      const previousQueryData =
        queryClient.getQueryData<RecordsWithTimestamp[]>([
          'instancesBatch',
          'savedRecords',
          list,
        ]) || [];

      queryClient.setQueryData(
        ['instancesBatch', 'savedRecords', key],
        previousQueryData.filter(item => item.id !== entry.id)
      );
    }
  };

  const handleUnauthorizedAction = ({
    showNotification,
  }: SaveToolButtonCallbacks) => {
    showNotification({ isSelected });

    return setSavedInstances(prevState => {
      if (isSelected) {
        removeSavedInstanceFromCache(prevState);

        return prevState.filter(instance => instance.instanceId !== entry.id);
      }

      return addEntryToDashboardList(
        {
          instanceId: entry.id,
          timestamp: `${DateTime.now()}`,
        },
        prevState
      );
    });
  };

  const handleAuthorizedAction = ({
    setItemSelected,
    showNotification,
  }: SaveToolButtonCallbacks) => {
    if (isSelected) {
      unsaveInstanceMutation.mutate(
        { savedInstances: [savedInstancesCompactList[entry[idKey]]] },
        {
          onSuccess: () => {
            removeSavedInstanceFromCache(savedRecordsQuery.data);
            refetchSavedInstancesCompactList();
          },
          onError: () => {
            setItemSelected(true);
            showNotification({ isSelected: true, isError: true });
          },
        }
      );
    } else {
      saveInstanceMutation.mutate(
        { savedInstances: [{ instanceId: entry[idKey] }] },
        {
          onSuccess: () => refetchSavedInstancesCompactList(),
          onError: () => {
            setItemSelected(false);
            showNotification({ isSelected: false, isError: true });
          },
        }
      );
    }
  };

  return (
    <SaveToolButton
      isSelected={isSelected}
      isDisabled={
        saveInstanceMutation.isLoading ||
        unsaveInstanceMutation.isLoading ||
        isSavedInstancesCompactInitialLoading
      }
      onAuthorizedAction={handleAuthorizedAction}
      onUnauthorizedAction={handleUnauthorizedAction}
    />
  );
};
